import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import type { User, Cliente } from "../types";

interface AuthContextType {
  user: User | null;
  cliente: Cliente | null;
  loading: boolean;
  isImpersonating: boolean;
  login: (email: string, password: string) => Promise<{ redirectTo: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  salirSuplantacion: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

function mapClienteFromApi(raw: Record<string, unknown> | null): Cliente | null {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: Number(raw.id),
    razon_social: String(raw.razonSocial ?? raw.razon_social ?? ""),
    cif: String(raw.cif ?? ""),
    direccion: String(raw.direccion ?? ""),
    poblacion: String(raw.poblacion ?? ""),
    codigo_postal: String(raw.codigoPostal ?? raw.codigo_postal ?? ""),
    pais: String(raw.pais ?? "España"),
    persona_contacto: String(raw.personaContacto ?? raw.persona_contacto ?? ""),
    telefono: String(raw.telefono ?? ""),
    email_contacto: String(raw.emailContacto ?? raw.email_contacto ?? ""),
    numero_poliza: raw.numeroPoliza != null ? String(raw.numeroPoliza) : (raw.numero_poliza != null ? String(raw.numero_poliza) : null),
    metodo_pago: (raw.metodoPago ?? raw.metodo_pago ?? "tpv") as "tpv" | "transferencia",
    estado: (raw.estado ?? "pendiente") as Cliente["estado"],
    created_at: String(raw.createdAt ?? raw.created_at ?? ""),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get<{
        user: User;
        cliente?: Record<string, unknown>;
        isImpersonating?: boolean;
      }>("/auth/me");
      setUser(data.user);
      setCliente(mapClienteFromApi(data.cliente ?? null));
      setIsImpersonating(!!data.isImpersonating);
    } catch {
      setUser(null);
      setCliente(null);
      setIsImpersonating(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = useCallback(
    async (email: string, password: string): Promise<{ redirectTo: string }> => {
      const { data } = await api.post<{ user: User; redirectTo: string }>("/auth/login", {
        email,
        password,
      });
      setUser(data.user);
      await refreshUser();
      return { redirectTo: data.redirectTo };
    },
    [refreshUser]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
      setCliente(null);
      setIsImpersonating(false);
      window.location.href = "/login";
    }
  }, []);

  const salirSuplantacion = useCallback(async () => {
    await api.post("/auth/salir-suplantacion");
    await refreshUser();
    navigate("/admin");
  }, [navigate, refreshUser]);

  const value: AuthContextType = {
    user,
    cliente,
    loading,
    isImpersonating,
    login,
    logout,
    refreshUser,
    salirSuplantacion,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
}
